<template>
  <div ref="root" :class="bannerClasses">
    <template v-if="isContentsInsuranceVariant">
      <div class="tw-w-full md:tw-hidden">
        <ContentsInsurancePromoCard :city-name="cityName" />
      </div>

      <div
        class="insurance-banner tw-hidden tw-w-full tw-rounded-2xl tw-px-[40px] tw-py-[26px] md:tw-block"
      >
        <div class="insurance-banner__panel" aria-hidden="true"></div>
        <div
          class="tw-relative tw-z-[1] tw-flex tw-flex-col tw-items-start tw-gap-[12px] tw-text-left md:tw-items-center md:tw-text-center"
        >
          <h2
            class="tw-font-inter tw-mb-0 tw-max-w-[320px] tw-text-[26px] tw-font-normal tw-leading-[38px] tw-text-white md:tw-max-w-[640px]"
          >
            <span class="tw-font-bold">FREE Contents Insurance</span> included with every UniHomes utilities package
            <button
              ref="toggle"
              type="button"
              class="tw-inline-flex tw-h-[20px] tw-w-[20px] tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-align-middle"
              aria-label="Show contents insurance information"
              :aria-expanded="showTooltip ? 'true' : 'false'"
              @click.stop="toggleTooltip"
            >
              <img
                :src="cdnAsset('svg/uh-info-icon-white.svg')"
                alt=""
                class="tw-h-[20px] tw-w-[20px]"
                aria-hidden="true"
              />
            </button>
          </h2>

          <div
            class="tw-flex tw-items-center tw-gap-[7px] md:tw-justify-center"
          >
            <img
              v-for="icon in utilityIcons"
              :key="icon.alt"
              :src="cdnAsset(icon.path)"
              :alt="icon.alt"
              class="tw-h-[42px] tw-w-[42px]"
            />

            <div
              class="tw-relative tw-ml-[4px] tw-h-[42px] tw-w-[42px] tw-shrink-0 tw-overflow-visible"
            >
              <div class="tw-absolute tw-inset-0">
                <span
                  aria-hidden="true"
                  class="tw-absolute -tw-inset-[4px] tw-rounded-full tw-bg-white/20"
                ></span>
                <span
                  aria-hidden="true"
                  class="tw-absolute tw-inset-0 tw-rounded-full tw-bg-white"
                ></span>
                <img
                  :src="cdnAsset(shieldIconPath)"
                  alt="Contents insurance shield"
                  class="tw-relative tw-h-full tw-w-full tw-object-contain tw-p-[10px]"
                />
              </div>
              <span
                class="free-badge tw-absolute tw-left-[23px] tw-top-[-8px] tw-z-10 tw-inline-flex tw-h-[18px] tw-min-w-[30px] tw-items-center tw-justify-center tw-rounded-[999px] tw-px-[5px] tw-text-[10px] tw-font-[800] tw-leading-[15px] tw-text-white"
              >
                FREE
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-show="showTooltip"
        ref="panel"
        class="tw-absolute tw-z-[10]"
        :style="tooltipStyle"
      >
        <div
          class="tw-relative tw-rounded-[8px] tw-bg-[#3B4143] tw-p-[8px] tw-shadow-[0_4px_24px_0_rgba(0,0,0,0.40)]"
        >
          <p
            class="tw-font-inter tw-mb-0 tw-text-left tw-text-[16px] tw-font-[400] tw-leading-[24px] tw-text-white"
          >
            Provided alongside your UniHomes utilities package at no additional
            cost to you.
          </p>
          <span
            class="tw-absolute tw-bottom-[-6px] tw-h-[12px] tw-w-[12px] -tw-translate-x-1/2 tw-rotate-45 tw-bg-[#3B4143]"
            :style="{
              left: 'var(--contents-insurance-tooltip-arrow-left, 50%)',
            }"
          ></span>
        </div>
      </div>
    </template>

    <template v-else>
      <h2
        class="tw-font-inter tw-mb-[12px] tw-w-[297px] tw-text-[26px] tw-font-bold tw-leading-[38px] tw-text-white"
      >
        All prices shown include bills
      </h2>
      <div class="tw-mb-[12px] tw-flex tw-space-x-[7px]">
        <img
          v-for="icon in utilityIcons"
          :key="icon.alt"
          :src="cdnAsset(icon.path)"
          :alt="icon.alt"
          class="tw-h-[42px] tw-w-[42px]"
        />
      </div>
      <h3
        class="tw-font-inter tw-mb-[12px] tw-text-[20px] tw-font-bold tw-leading-[28px] tw-text-white"
      >
        Enjoy stress-free student living!
      </h3>
    </template>
  </div>
</template>

<script>
import ContentsInsurancePromoCard from '@components/properties-search/components/ContentsInsurancePromoCard.vue';

export default {
  name: 'SearchResultsUtilityBanner',
  components: {
    ContentsInsurancePromoCard,
  },
  props: {
    cityName: {
      default: null,
      type: String,
    },
    variant: {
      default: 'bills',
      type: String,
      validator: (value) => ['bills', 'contents-insurance'].includes(value),
    },
  },
  data() {
    return {
      showTooltip: false,
      tooltipStyle: {},
      billsUtilityIcons: [
        { alt: 'Flame Icon', path: 'svg/uh-flame-icon.svg' },
        { alt: 'Bolt Icon', path: 'svg/uh-bolt-icon.svg' },
        { alt: 'Wifi Icon', path: 'svg/uh-wifi-icon.svg' },
        { alt: 'Water Icon', path: 'svg/uh-water-icon.svg' },
      ],
      insuranceUtilityIcons: [
        { alt: 'Flame Icon', path: 'svg/uh-flame-icon-insurance.svg' },
        { alt: 'Bolt Icon', path: 'svg/uh-bolt-icon-insurance.svg' },
        { alt: 'Wifi Icon', path: 'svg/uh-wifi-icon-insurance.svg' },
        { alt: 'Water Icon', path: 'svg/uh-water-icon-insurance.svg' },
      ],
    };
  },
  computed: {
    bannerClasses() {
      return [
        this.isContentsInsuranceVariant
          ? 'tw-relative tw-z-10 tw-w-full tw-mb-[24px] md:tw-mb-[36px]'
          : 'tw-relative tw-z-10 tw-mb-[18px] tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-2xl tw-text-center md:tw-mb-[36px]',
        this.isContentsInsuranceVariant
          ? ''
          : 'bg-img-utilities tw-bg-[#00A2E1] tw-pb-[94px] tw-pt-[87px] md:tw-pb-[25px] md:tw-pt-[37px]',
      ];
    },
    isContentsInsuranceVariant() {
      return this.variant === 'contents-insurance';
    },
    shieldIconPath() {
      return 'svg/uh-shield-check-icon-clean.svg';
    },
    utilityIcons() {
      return this.isContentsInsuranceVariant
        ? this.insuranceUtilityIcons
        : this.billsUtilityIcons;
    },
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleEscapeKey);
    window.addEventListener('resize', this.updateTooltipPosition);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleEscapeKey);
    window.removeEventListener('resize', this.updateTooltipPosition);
  },
  methods: {
    closeTooltip() {
      this.showTooltip = false;
    },
    handleDocumentClick(event) {
      if (!this.showTooltip || !this.isContentsInsuranceVariant) {
        return;
      }

      if (!this.$refs.root?.contains(event.target)) {
        this.closeTooltip();
      }
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape') {
        this.closeTooltip();
      }
    },
    toggleTooltip() {
      this.showTooltip = !this.showTooltip;

      if (this.showTooltip) {
        this.$nextTick(this.updateTooltipPosition);
      }
    },
    updateTooltipPosition() {
      if (
        !this.showTooltip ||
        !this.isContentsInsuranceVariant ||
        !this.$refs.root ||
        !this.$refs.toggle ||
        !this.$refs.panel
      ) {
        return;
      }

      const rootRect = this.$refs.root.getBoundingClientRect();
      const toggleRect = this.$refs.toggle.getBoundingClientRect();
      const panel = this.$refs.panel;
      const horizontalGutter = window.innerWidth >= 768 ? 24 : 16;
      const tooltipGap = 12;
      const arrowPadding = 32;
      const targetWidth =
        window.innerWidth >= 768
          ? Math.min(400, Math.max(window.innerWidth - horizontalGutter * 2, 0))
          : Math.max(window.innerWidth - horizontalGutter * 2, 0);

      panel.style.width = `${targetWidth}px`;
      const panelRect = panel.getBoundingClientRect();
      const toggleCenter = toggleRect.left + toggleRect.width / 2;
      const panelLeft = Math.min(
        Math.max(toggleCenter - panelRect.width / 2, horizontalGutter),
        Math.max(
          horizontalGutter,
          window.innerWidth - panelRect.width - horizontalGutter
        )
      );
      const panelTop =
        toggleRect.top - rootRect.top - panelRect.height - tooltipGap;
      const arrowLeft = Math.min(
        Math.max(toggleCenter - panelLeft, arrowPadding),
        panelRect.width - arrowPadding
      );

      this.tooltipStyle = {
        '--contents-insurance-tooltip-arrow-left': `${arrowLeft}px`,
        left: `${panelLeft - rootRect.left}px`,
        top: `${panelTop}px`,
        width: `${targetWidth}px`,
      };
    },
  },
};
</script>
